/**
 * ResearchTeamsPage — /omega/research/teams
 * Sprint 37 Phase 41.A: Spawn and monitor ResearchConductor tasks.
 */
import { useState } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { isOmegaAdmin } from '@/features/omega/adminMode';
import { apiFetch } from '@/api/http';

interface ConductorRow {
  conductor_id: string;
  vertical: string;
  agenda: string | null;
  status: 'Running' | 'Completed' | 'Stopped' | 'Error';
  atoms_inserted: number;
  atoms_per_min: number;
  started_at: string;
  finished_at: string | null;
  layer: string | null;
}

interface SpawnRequest {
  vertical: string;
  count?: number;
  agenda?: string;
  layer?: string;
}

// The full 12-vertical GI taxonomy. Names are the canonical kebab-case
// serialization of the `GICategory` enum (gi-action-types) — the backend
// learning loop only recognises these exact strings, so they must match.
const VERTICALS = [
  'commerce-and-retail',
  'work-and-productivity',
  'industry-and-manufacturing',
  'financial',
  'health-and-wellbeing',
  'technology-and-infrastructure',
  'knowledge-and-education',
  'media-and-entertainment',
  'real-estate-and-property',
  'government-and-civic',
  'mobility-and-transport',
  'personal-and-lifestyle',
];

const LAYERS = [
  { value: 'causality', label: 'Causality' },
  { value: 'risk-and-uncertainty', label: 'Risk & Uncertainty' },
  { value: 'behavioral-science', label: 'Behavioral Science' },
  { value: 'regulatory-and-legal', label: 'Regulatory & Legal' },
  { value: 'ethics', label: 'Ethics' },
  { value: 'information', label: 'Information' },
  { value: 'temporal-reasoning', label: 'Temporal Reasoning' },
  { value: 'incentives', label: 'Incentives' },
  { value: 'language-sovereignty', label: 'Language Sovereignty' },
];

function StatusBadge({ status }: { status: ConductorRow['status'] }) {
  const colors: Record<string, string> = {
    Running: '#2563eb',
    Completed: '#16a34a',
    Stopped: '#6b7280',
    Error: '#dc2626',
  };
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: 4,
      background: colors[status] ?? '#6b7280', color: '#fff',
      fontSize: 12, fontWeight: 600,
    }}>
      {status}
    </span>
  );
}

export function ResearchTeamsPage() {
  if (!isOmegaAdmin()) return <Navigate to="/" replace />;

  const qc = useQueryClient();

  const { data: conductors = [], isLoading } = useQuery<ConductorRow[]>({
    queryKey: ['research-conductors'],
    queryFn: () => apiFetch<ConductorRow[]>('/admin/research/conductors'),
    refetchInterval: (q) => {
      const rows = q.state.data ?? [];
      return rows.some(r => r.status === 'Running') ? 10_000 : false;
    },
  });

  const spawnMutation = useMutation({
    mutationFn: (req: SpawnRequest) =>
      apiFetch('/admin/research/spawn', { method: 'POST', body: req }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['research-conductors'] }),
  });

  const stopMutation = useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/admin/research/conductors/${id}/stop`, { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['research-conductors'] }),
  });

  // Sprint 42 D3 — click-to-steer: record a founder directive on a team (A4 endpoint).
  const steerMutation = useMutation({
    mutationFn: ({ id, directive }: { id: string; directive: string }) =>
      apiFetch(`/admin/research/conductors/${id}/steer`, { method: 'POST', body: { directive } }),
  });

  const refreshMutation = useMutation({
    mutationFn: () =>
      apiFetch<{ ok: boolean; verticals_refreshed: number; layer_counts_updated: number }>(
        '/admin/research/refresh-g1',
        { method: 'POST' }
      ),
  });

  const [vertical, setVertical] = useState<string>('commerce-and-retail');
  const [count, setCount] = useState(100);
  const [agenda, setAgenda] = useState('');
  const [layer, setLayer] = useState<string>('');

  const handleSpawn = () => {
    const req: SpawnRequest = { vertical, count };
    if (agenda) req.agenda = agenda;
    if (layer) req.layer = layer;
    spawnMutation.mutate(req);
  };

  return (
    <div style={{ padding: 24, maxWidth: 1100 }}>
      <h1 style={{ marginBottom: 4 }}>Research Teams</h1>
      <p style={{ color: '#6b7280', marginBottom: 20 }}>
        Phase 41.A — Spawn research-team Conductors for active verticals.{' '}
        <Link to="/omega/learning/gates">← Learning Gates</Link>
        {' · '}
        <button
          onClick={() => refreshMutation.mutate()}
          disabled={refreshMutation.isPending}
          style={{ background: 'none', border: 'none', color: '#2563eb', cursor: 'pointer', fontSize: 13, padding: 0, textDecoration: 'underline' }}
        >
          {refreshMutation.isPending ? 'Refreshing G-1…' : 'Refresh G-1 data'}
        </button>
        {refreshMutation.isSuccess && refreshMutation.data && (
          <span style={{ color: '#16a34a', marginLeft: 8, fontSize: 12 }}>
            ✓ {refreshMutation.data.verticals_refreshed}v / {refreshMutation.data.layer_counts_updated} layers updated
          </span>
        )}
      </p>

      {/* Spawn form */}
      <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, padding: 16, marginBottom: 24 }}>
        <h3 style={{ margin: '0 0 12px' }}>Spawn Research Team</h3>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 4 }}>Vertical</label>
            <select value={vertical} onChange={e => setVertical(e.target.value)}
              style={{ padding: '6px 10px', borderRadius: 4, border: '1px solid #d1d5db', fontSize: 13 }}>
              {VERTICALS.map(v => <option key={v} value={v}>{v}</option>)}
            </select>
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 4 }}>Target atoms</label>
            <input type="number" value={count} onChange={e => setCount(Number(e.target.value))}
              min={1} max={1000} style={{ width: 80, padding: '6px 10px', borderRadius: 4, border: '1px solid #d1d5db', fontSize: 13 }} />
          </div>
          <div style={{ flex: 1, minWidth: 200 }}>
            <label style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 4 }}>Agenda (optional)</label>
            <input type="text" value={agenda} onChange={e => setAgenda(e.target.value)}
              placeholder="e.g. focus on regulation, pricing benchmarks"
              style={{ width: '100%', padding: '6px 10px', borderRadius: 4, border: '1px solid #d1d5db', fontSize: 13 }} />
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 4 }}>Layer (optional)</label>
            <select value={layer} onChange={e => setLayer(e.target.value)}
              style={{ padding: '6px 10px', borderRadius: 4, border: '1px solid #d1d5db', fontSize: 13 }}>
              <option value="">— none —</option>
              {LAYERS.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
            </select>
          </div>
          <button onClick={handleSpawn} disabled={spawnMutation.isPending}
            style={{ padding: '8px 16px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
            {spawnMutation.isPending ? 'Spawning…' : 'Spawn Team'}
          </button>
        </div>
        {spawnMutation.isError && <p style={{ color: '#dc2626', marginTop: 8, fontSize: 12 }}>Spawn failed. Check console.</p>}
        {spawnMutation.isSuccess && <p style={{ color: '#16a34a', marginTop: 8, fontSize: 12 }}>Team spawned.</p>}
      </div>

      {/* Conductor table */}
      {isLoading ? <p>Loading conductors…</p> : conductors.length === 0 ? (
        <p style={{ color: '#6b7280' }}>No conductors yet. Spawn a team above.</p>
      ) : (
        <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 13 }}>
          <thead>
            <tr style={{ background: '#f3f4f6' }}>
              {['Vertical', 'Layer', 'Agenda', 'Status', 'Atoms', 'Rate (atoms/min)', 'Started', ''].map(h => (
                <th key={h} style={{ textAlign: 'left', padding: '8px 12px', border: '1px solid #e5e7eb', fontWeight: 600 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {conductors.map(row => (
              <tr key={row.conductor_id}>
                <td style={{ padding: '8px 12px', border: '1px solid #e5e7eb' }}>{row.vertical}</td>
                <td style={{ padding: '8px 12px', border: '1px solid #e5e7eb', color: '#6b7280' }}>{row.layer ?? '—'}</td>
                <td style={{ padding: '8px 12px', border: '1px solid #e5e7eb', color: '#6b7280' }}>{row.agenda ?? '—'}</td>
                <td style={{ padding: '8px 12px', border: '1px solid #e5e7eb' }}><StatusBadge status={row.status} /></td>
                <td style={{ padding: '8px 12px', border: '1px solid #e5e7eb', fontVariantNumeric: 'tabular-nums' }}>{(row.atoms_inserted ?? 0).toLocaleString()}</td>
                <td style={{ padding: '8px 12px', border: '1px solid #e5e7eb' }}>{(row.atoms_per_min ?? 0).toFixed(1)}</td>
                <td style={{ padding: '8px 12px', border: '1px solid #e5e7eb', color: '#6b7280' }}>{new Date(row.started_at).toLocaleTimeString()}</td>
                <td style={{ padding: '8px 12px', border: '1px solid #e5e7eb' }}>
                  {row.status === 'Running' && (
                    <>
                      <button onClick={() => stopMutation.mutate(row.conductor_id)}
                        disabled={stopMutation.isPending}
                        style={{ padding: '4px 10px', background: '#fee2e2', color: '#dc2626', border: '1px solid #fca5a5', borderRadius: 4, cursor: 'pointer', fontSize: 12 }}>
                        Stop
                      </button>
                      <button onClick={() => {
                        const directive = window.prompt('Steer this team — directive (e.g. "dig deeper into pricing", "ignore X"):');
                        if (directive && directive.trim()) steerMutation.mutate({ id: row.conductor_id, directive: directive.trim() });
                      }}
                        disabled={steerMutation.isPending}
                        style={{ marginLeft: 6, padding: '4px 10px', background: '#dbeafe', color: '#2563eb', border: '1px solid #93c5fd', borderRadius: 4, cursor: 'pointer', fontSize: 12 }}>
                        Steer
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
